import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { PRAXIS, SPRECHZEITEN, LEISTUNGEN } from "../data/praxis";
import { api } from "../utils/api";

/**
 * Zentraler Store für dynamische Praxis-Inhalte.
 *  - `settings` (Kontakt-Daten wie name, phone, email, address, ...) — flach, camelCase
 *  - `hours`     (Sprechzeiten [{ day, time }, ...])
 *  - `services`  (Leistungen [{ slug, title, icon, short, bullets, note, ... }, ...])
 *
 * Beim Mount wird `/api/site-config.php` einmal abgeholt.
 * Bei Fehler/leerer Antwort greift der statische Fallback aus `praxis.js`.
 */

// Statischer Fallback im praxis.js-Format
const FALLBACK_SETTINGS = {
  name: PRAXIS.name,
  role: PRAXIS.role,
  street: PRAXIS.street,
  zip: PRAXIS.zip,
  city: PRAXIS.city,
  phone: PRAXIS.phone,
  phoneRaw: PRAXIS.phoneRaw,
  fax: PRAXIS.fax,
  faxRaw: PRAXIS.faxRaw,
  email: PRAXIS.email,
  web: PRAXIS.web,
  webUrl: PRAXIS.webUrl,
};

const FALLBACK_HOURS = SPRECHZEITEN.map((s, i) => ({
  id: -i - 1,
  day: s.day,
  time: s.time,
  sort_order: i,
}));

const FALLBACK_SERVICES = LEISTUNGEN.map((l, i) => ({
  id: -i - 1,
  slug: l.slug,
  title: l.title,
  icon: l.icon,
  short: l.short,
  bullets: l.bullets || [],
  note: l.note || "",
  has_page: !!l.hasPage,
  page_path: l.hasPage ? l.slug : "",
  is_new: !!l.isNew,
  new_label: l.newLabel || "",
  sort_order: i,
}));

// snake_case (DB) → camelCase (Frontend) — nur für settings
function toCamelSettings(raw) {
  return {
    name: raw.name ?? "",
    role: raw.role ?? "",
    street: raw.street ?? "",
    zip: raw.zip ?? "",
    city: raw.city ?? "",
    phone: raw.phone ?? "",
    phoneRaw: raw.phone_raw ?? "",
    fax: raw.fax ?? "",
    faxRaw: raw.fax_raw ?? "",
    email: raw.email ?? "",
    web: raw.web ?? "",
    webUrl: raw.web_url ?? "",
  };
}

const PraxisContext = createContext({
  settings: FALLBACK_SETTINGS,
  hours: FALLBACK_HOURS,
  services: FALLBACK_SERVICES,
  loading: true,
  refresh: () => {},
});

export function PraxisProvider({ children }) {
  const [state, setState] = useState({
    settings: FALLBACK_SETTINGS,
    hours: FALLBACK_HOURS,
    services: FALLBACK_SERVICES,
    loading: true,
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.getSiteConfig();
      if (data && data.ok) {
        setState({
          settings: (data.settings && Object.keys(data.settings).length > 0)
            ? toCamelSettings(data.settings) : FALLBACK_SETTINGS,
          hours: (Array.isArray(data.hours) && data.hours.length > 0)
            ? data.hours : FALLBACK_HOURS,
          services: (Array.isArray(data.services) && data.services.length > 0)
            ? data.services : FALLBACK_SERVICES,
          loading: false,
        });
        return;
      }
    } catch { /* fällt auf Fallback */ }
    setState((s) => ({ ...s, loading: false }));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <PraxisContext.Provider value={{ ...state, refresh }}>
      {children}
    </PraxisContext.Provider>
  );
}

export function usePraxis() {
  return useContext(PraxisContext);
}
